import {readable, writable} from "svelte/store";

// These stored variables are used to represent the current state of the feed
// and are needed when the user opens a document and then goes back to the feed
// (and these stored variables then make sure that the feed is the same when
// they go back)
export const request = writable({});
export const query = writable('');
export const previous_query = writable('');
export const previous_date_range = writable({});

// Total number of documents available
export const total = writable(null);
// The documents shown in the feed
export const docs = writable([]);

// Total number of agenda items available
export const meetings_total = writable(null);
// The agenda items shown in the feed
export const meetings = writable([]);

// All categories in the correct order; this structure is similar to the
// response of an empty API search request, but we hardcode it to avoid
// unnecessary calls and logic; this store is used to retrieve all available
// categories, needed to create $facet_categories and $categories
export const categories_all = writable([
    {
      "key": "Parlementaire documenten"
    },
    {
      "key": "Parlementaire documenten|Documenten Tweede Kamer"
    },
    {
      "key": "Parlementaire documenten|Documenten Tweede Kamer|Kamerbrief"
    },
    {
      "key": "Parlementaire documenten|Documenten Tweede Kamer|Kamervraag"
    },
    {
      "key": "Parlementaire documenten|Documenten Tweede Kamer|Handeling (plenaire verslagen)"
    },
    {
      "key": "Parlementaire documenten|Documenten Tweede Kamer|Besluitenlijst"
    },
    {
      "key": "Parlementaire documenten|Documenten Tweede Kamer|Debattranscriptie"
    },
    {
      "key": "Parlementaire documenten|Documenten Tweede Kamer|Motie"
    },
    {
      "key": "Parlementaire documenten|Documenten Tweede Kamer|Amendement"
    },
    {
      "key": "Parlementaire documenten|Documenten Tweede Kamer|Commissieverslag"
    },
    {
      "key": "Parlementaire documenten|Documenten Tweede Kamer|Verslag"
    },
    {
      "key": "Parlementaire documenten|Documenten Tweede Kamer|Stemming"
    },
    {
      "key": "Parlementaire documenten|Documenten Tweede Kamer|Overig"
    },
    {
      "key": "Parlementaire documenten|Documenten Eerste Kamer"
    },
    {
      "key": "Parlementaire documenten|Documenten Eerste Kamer|Kamerbrief"
    },
    {
      "key": "Parlementaire documenten|Documenten Eerste Kamer|Kamervraag"
    },
    {
      "key": "Parlementaire documenten|Documenten Eerste Kamer|Handeling (plenaire verslagen)"
    },
    {
      "key": "Parlementaire documenten|Documenten Eerste Kamer|Motie"
    },
    {
      "key": "Parlementaire documenten|Documenten Eerste Kamer|Amendement"
    },
    {
      "key": "Parlementaire documenten|Documenten Eerste Kamer|Verslag"
    },
    {
      "key": "Parlementaire documenten|Documenten Eerste Kamer|Agenda"
    },
    {
      "key": "Parlementaire documenten|Documenten Eerste Kamer|Overig"
    },
    {
      "key": "Parlementaire documenten|Staatsblad"
    },
    {
      "key": "Parlementaire documenten|Advies Raad van State"
    },
    {
      "key": "Internetconsultaties"
    },
    {
      "key": "Internetconsultaties|Consultaties"
    },
    {
      "key": "Rijksoverheid"
    },
    {
      "key": "Rijksoverheid|Ministerraad"
    },
    {
      "key": "Europees Parlement"
    },
    {
      "key": "Europees Parlement|Documenten Europese Commissie"
    },
    {
      "key": "Europees Parlement|Documenten Europese Commissie|COM - documenten"
    },
    {
      "key": "Europees Parlement|Documenten Europese Commissie|JOIN - documenten"
    },
    {
      "key": "Europees Parlement|Documenten Europese Commissie|SEC - documenten"
    },
    {
      "key": "Europees Parlement|Aangenomen teksten"
    },
    {
      "key": "Europees Parlement|Debatten"
    },
    {
      "key": "Media"
    },
    {
      "key": "Media|Online nieuws"
    },
    {
      "key": "Media|Persberichten"
    },
    {
      "key": "1848"
    },
    {
      "key": "1848|1848 updates"
    },
    {
      "key": "1848|Factsheets"
    },
    {
      "key": "1848|Nieuws"
    }
]);

// Nested object of categories with doc_counts, e.g.:
//{
//  'Parlementaire documenten': {
//    doc_count: 282,
//    'Documenten Tweede Kamer': {
//      doc_count: 271,
//      Kamerbrief: ...,
export const facet_categories = writable({});

// Flat object with booleans for all categories (thus written as
// 'Parlementaire documenten|Documenten Tweede Kamer|Motie'); i.e. which
// categories the user or dossier has selected/checked
export const categories = writable({});

export const edit_dossier_categories = writable({});

export const select_all = writable(false);

// Stores all the dossiers of a user
export const dossiers = writable([]);
export const start_date = writable(null);
export const end_date = writable(null);
export const start_date_string = writable('');
export const end_date_string = writable('');
// Number of documents to load in the document feed initially and when
// scrolling
export const size = readable(25);
// Boolean which tells the infinite scroll if there are more items to load
export const has_more = writable(true);

// Whether a user (un)checked a category filter
export const clicked_category = writable(false);
// Whether a user changed the date
export const clicked_date = writable(false);
// '', 'all' or the '<id>' of a dossier
export const current_dossier_id = writable('');

export const add_dossier_open = writable(false);
// whether the filter accordion should be open or not
export const dossier_accordion_active = writable('');

export const doc_feed_scroll_position = writable(0);
export const agenda_feed_scroll_position = writable(0);

// Set last_refresh to the current timestamp after a page load/change. We use
// it to set an interval to check if there are new documents every 30 seconds
export const last_refresh = writable('');

// Every 30 seconds we check for new documents, if there are new documents,
// this stores the total number of them
export const total_new_docs = writable(0);

// Stores the URL parameters ('?zoek=' or '?dossier=') needed to create correct
// links to the feed on a document page or when switching feeds via the menu
export const url_search_params_string = writable('');

// By default we want to run the requests in the load functions
// (do_load == true), but not always (e.g., when navigating back from a
// document to the feed)
export const do_load = writable(true);
export const do_load_layout = writable(true);
